import { SimpleSearchFilter } from 'module/api/filter/types';
import { useTranslate } from 'module/hooks/use-translation';
import { logger } from 'module/logger';
import { Grid as BaseGrid } from 'module/ui/components/atoms/grid/grid';
import { humanise } from 'module/ui/components/atoms/humanised-date';
import { getRandomColor, Tag } from '@rocketlane/ui';
import { ColDef } from 'ag-grid-community';
import {
  KnowledgeBaseAiPageContent,
  KnowledgeBaseContent,
  KnowledgeBaseFileContent,
  KnowledgeBaseFolderLite,
  KnowledgeBaseTreeFolder,
} from 'api/knowledge-base/types';
import moment from 'moment';
import { useUpdateAiPageMutation } from 'queries/knowledge-base';
import { FC, useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { KnowledgeBaseFileViewer } from '../file-viewer';
import { ActionsCell } from './renderers/action-cell';
import { NameCell } from './renderers/name-cell';
import { FolderItem, FolderItemContext } from './types';
import { EditFilterConditionDrawer } from '../add-ai-pages/edit-filter-condition-drawer';
import {
  getAiPageType,
  getContentType,
  getFolderType,
  getFolderTypeLabel,
} from '../file-viewer/icon-for-file-type';

const Grid = styled(BaseGrid)`
  flex: 1;
  .ag-cell {
    border: none !important;
  }
  .ag-cell-focus {
    cursor: pointer;
    border: none !important;
    background-color: transparent !important;
  }
  --ag-cell-widget-spacing: 16px;
  --ag-row-group-indent-size: 16px;
`;
const GridWrapper = styled.div<{ hasNoTreeStructure: boolean }>`
  ${({ hasNoTreeStructure }) =>
    hasNoTreeStructure &&
    css`
      .ag-row-group-leaf-indent.ag-row-group-indent-0 {
        padding-left: 0 !important;
      }
    `};
  padding: var(--spacing-03) var(--spacing-06);
  height: 100%;
  overflow: auto;
`;
const Wrapper = styled.div`
  height: 100%;
  overflow: hidden;
  position: relative;
`;

interface FolderContentListProps {
  knowledgeBase: KnowledgeBaseFolderLite;
  folderTree: KnowledgeBaseTreeFolder;
  deleteFolder: (folderId: number) => Promise<void>;
  deleteFile: (folderId: number, fileId: number) => Promise<void>;
}

const flattenTree = (nodes: FolderItem[], parentPath: string[] = []): any[] =>
  nodes.flatMap((node) => {
    const currentPath = [...parentPath, node.id];
    const flatNode = { ...node, path: currentPath };
    const children = node.children
      ? flattenTree(node.children, currentPath)
      : [];

    return [flatNode, ...children];
  });
const fileToFolderItem = (
  content: KnowledgeBaseFileContent,
  rootFolderContext?: FolderItemContext,
  parentContext?: FolderItemContext,
) => {
  return {
    id: content.contentId.toString(),
    name: content.attachment.name,
    type: 'file',
    url: content.attachment.location,
    actualContentType: content.contentType,
    createdAt: content.createdAt,
    rootFolderContext,
    parentContext,
    contentType: getContentType(content),
    originalContent: content,
  };
};

const aiPageToFolderItem = (
  content: KnowledgeBaseAiPageContent,
  rootFolderContext?: FolderItemContext,
  parentContext?: FolderItemContext,
) => {
  return {
    id: content.contentId.toString(),
    name: content.template.templateName,
    type: 'file',
    actualContentType: content.contentType,
    createdAt: content.createdAt,
    template: content.template,
    filter: content.pageFilters?.originalFilter,
    rootFolderContext,
    parentContext,
    contentType: getContentType(content),
    originalContent: content,
  };
};
const contentsToFolderItem = (
  contents: KnowledgeBaseContent[],
  rootFolderContext?: FolderItemContext,
  parentContext?: FolderItemContext,
) => {
  return contents.map((content) =>
    content.contentType === 'AI_PAGE'
      ? aiPageToFolderItem(
          content as KnowledgeBaseAiPageContent,
          rootFolderContext,
          parentContext,
        )
      : fileToFolderItem(
          content as KnowledgeBaseFileContent,
          rootFolderContext,
          parentContext,
        ),
  );
};
const foldersToFolderItem = (
  folders: KnowledgeBaseTreeFolder[],
  rootFolderContext?: FolderItemContext,
  parentContext?: FolderItemContext,
) => {
  return folders.map((folder) => ({
    id: folder.folderId.toString(),
    name: folder.folderName,
    type: 'folder',
    contentType: getFolderType(folder),
    actualContentType: folder.folderType,
    rootFolderContext,
    parentContext,
    children: [
      ...foldersToFolderItem(
        folder.children,
        rootFolderContext ?? {
          contentType: folder.folderType,
          id: folder.folderId.toString(),
        },
        {
          contentType: folder.folderType,
          id: folder.folderId.toString(),
        },
      ),
      ...contentsToFolderItem(
        folder.contents,
        rootFolderContext ?? {
          contentType: folder.folderType,
          id: folder.folderId.toString(),
        },
        {
          contentType: folder.folderType,
          id: folder.folderId.toString(),
        },
      ),
    ],
  }));
};

export const FolderContentList: FC<FolderContentListProps> = ({
  knowledgeBase,
  folderTree,
  deleteFolder,
  deleteFile,
}) => {
  const translate = useTranslate();
  const [selectedFile, setSelectedFile] = useState<FolderItem | null>(null);
  const data = useMemo(() => {
    const rootFolderContext = {
      contentType: 'DEFAULT' as const,
      id: knowledgeBase.folderId.toString(),
    };

    return [
      ...foldersToFolderItem(
        folderTree.children,
        rootFolderContext,
        rootFolderContext,
      ),
      ...contentsToFolderItem(
        folderTree.contents,
        rootFolderContext,
        rootFolderContext,
      ),
    ];
  }, [folderTree, knowledgeBase.folderId]);

  const hasNoTreeStructure = folderTree.children.length === 0;
  const [editFilterConditionFolderItem, setEditFilterConditionFolderItem] =
    useState<FolderItem>();
  const closeEditFilterConditionDrawer = useCallback(() => {
    setEditFilterConditionFolderItem(undefined);
  }, []);
  const { mutateAsync: updateAiPage } = useUpdateAiPageMutation();
  const updateFilterCondition = useCallback(
    (filter: SimpleSearchFilter | null) => {
      try {
        updateAiPage({
          folder_id: Number(editFilterConditionFolderItem?.parentContext?.id),
          content_id: Number(editFilterConditionFolderItem?.id),
          filter,
        });
        closeEditFilterConditionDrawer();
      } catch (error) {
        logger.error('Error updating AI page filter', { error });
      }
    },
    [
      editFilterConditionFolderItem,
      updateAiPage,
      closeEditFilterConditionDrawer,
    ],
  );

  const colDefs: ColDef[] = useMemo(() => {
    return [
      {
        headerName: 'File type',
        field: 'contentType',
        cellRenderer: (params: any) => {
          if (params.value === '') {
            return <></>;
          }
          const value =
            params.data.type === 'folder'
              ? getFolderTypeLabel(params.data.actualContentType, translate)
              : params.data.actualContentType === 'AI_PAGE'
                ? getAiPageType(params.data.contentType, translate)
                : params.value;

          return (
            <Tag variant="basic" color={getRandomColor(value)}>
              {value}
            </Tag>
          );
        },
        width: 186,
      },
      {
        headerName: 'Date added',
        field: 'createdAt',
        width: 186,
        valueFormatter: (params: any) => {
          return params.value ? humanise(moment(params.value)) : '';
        },
      },
      {
        headerName: 'Added by',
        width: 186,
      },
      {
        headerName: '',
        width: 56,
        cellRenderer: ActionsCell,
        cellRendererParams: {
          onEdit: (data: FolderItem) => {
            setEditFilterConditionFolderItem(data);
          },
          onDelete: (data: FolderItem) => {
            if (data.type === 'folder') {
              deleteFolder(Number(data.id));
            } else {
              deleteFile(Number(data.parentContext?.id), Number(data.id));
            }
          },
        },
      },
    ];
  }, [deleteFile, deleteFolder, translate]);

  const handleCloseViewer = () => {
    setSelectedFile(null);
  };

  const rowData = useMemo(() => {
    const flattened = flattenTree(data);

    return flattened;
  }, [data]);

  return (
    <Wrapper>
      <GridWrapper hasNoTreeStructure={hasNoTreeStructure}>
        <Grid
          rowData={rowData}
          columnDefs={colDefs}
          treeData
          getDataPath={(data: any) => data.path}
          groupDefaultExpanded={-1}
          defaultColDef={{
            filter: false,
            sortable: false,
            resizable: false,
            wrapText: true,
            suppressMovable: true,
            suppressHeaderMenuButton: true,
          }}
          suppressContextMenu
          autoGroupColumnDef={{
            headerName: translate('name'),
            flex: 1,
            cellRendererParams: {
              suppressCount: true,
              innerRenderer: NameCell,
              innerRendererParams: {
                showFile: (data: FolderItem) => {
                  setSelectedFile(data);
                },
              },
            },
          }}
        />
      </GridWrapper>
      <EditFilterConditionDrawer
        file={editFilterConditionFolderItem}
        filter={editFilterConditionFolderItem?.filter}
        onDone={updateFilterCondition}
        visible={!!editFilterConditionFolderItem}
        onClose={closeEditFilterConditionDrawer}
      />
      {selectedFile && (
        <KnowledgeBaseFileViewer
          file={selectedFile}
          onClose={handleCloseViewer}
          onDelete={() => {
            deleteFile(
              Number(selectedFile.parentContext?.id),
              Number(selectedFile.id),
            );
            handleCloseViewer();
          }}
        />
      )}
    </Wrapper>
  );
};
